/**
 * OrganizerSubgroupSorterDialog — Three-panel drag-and-drop workspace for
 * sorting an organizer's achievements into subgroups.
 *
 * Layout (left → right):
 *   Left   : achievements inside the currently-selected subgroup
 *   Middle : list of subgroups (click to select, drop target for moves)
 *   Right  : unsorted (ungrouped) achievements — still inside the organizer
 *            but not yet placed into a subgroup
 *
 * Users can:
 *   - Drag an achievement from Right → a subgroup in Middle to bucket it
 *   - Drag an achievement from Left → a different subgroup in Middle to
 *     re-bucket it
 *   - Drag an achievement from Left → Right to drop it back to Ungrouped
 *   - Click a subgroup in Middle to focus the Left panel
 *   - Create a fresh subgroup via the "Add subgroup" button in the header
 *
 * Persistence uses the existing `/bulk-move` endpoint (targetType=subsection
 * or section) so no new backend work is required.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import { Loader2, Plus, Layers, Inbox, X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getSubgroupIconComp } from "./SubgroupManagerDialog";

const UNGROUPED_ID = "__ungrouped__";

/**
 * dnd-kit modifier that keeps the drag overlay centered on the cursor at all
 * times. Without this, dnd-kit preserves the initial click offset — but when
 * the DndContext is nested inside a Radix Dialog (which uses `transform` to
 * center itself), that offset is calculated in the wrong coordinate space and
 * the overlay appears way off to the side of the cursor.
 */
const snapCenterToCursor = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (draggingNodeRect && activatorEvent) {
    const activatorCoords = {
      x: "clientX" in activatorEvent ? activatorEvent.clientX : activatorEvent.touches?.[0]?.clientX ?? 0,
      y: "clientY" in activatorEvent ? activatorEvent.clientY : activatorEvent.touches?.[0]?.clientY ?? 0,
    };
    // Offset that dnd-kit would normally apply (top-left of node minus cursor)
    const offsetX = activatorCoords.x - draggingNodeRect.left;
    const offsetY = activatorCoords.y - draggingNodeRect.top;
    // Re-center the overlay on the cursor
    return {
      ...transform,
      x: transform.x + offsetX - draggingNodeRect.width / 2,
      y: transform.y + offsetY - draggingNodeRect.height / 2,
    };
  }
  return transform;
};

/**
 * Sort options for the sorter panels. Each entry defines the key label +
 * an extractor that returns a comparable value from an achievement. The
 * dropdown lets users pick a sort mode; a separate button toggles direction.
 */
const SORT_OPTIONS = [
  {
    value: "name",
    label: "Name",
    extract: (a) => (a.name || "").toLowerCase(),
    numeric: false,
  },
  {
    value: "score",
    label: "Score",
    extract: (a) => a.score ?? 0,
    numeric: true,
  },
  {
    value: "dlc",
    label: "DLC",
    // Empty string sinks base-game achievements to the top of an "asc" sort.
    extract: (a) => (a.dlcName || "").toLowerCase(),
    numeric: false,
  },
  {
    value: "unlocked",
    label: "Unlocked",
    extract: (a) => (a.isUnlocked ? 1 : 0),
    numeric: true,
  },
  {
    value: "rarity",
    label: "Rarity",
    // 999 sinks unknown-rarity entries to the bottom of an "asc" sort so
    // known rarity values compare cleanly first.
    extract: (a) => (a.rarity == null ? 999 : a.rarity),
    numeric: true,
  },
  {
    value: "earned",
    label: "Earned date",
    extract: (a) => (a.earnedAt ? Date.parse(a.earnedAt) : 0),
    numeric: true,
  },
];

function sortAchievements(list, sortBy, sortDir) {
  const opt = SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0];
  const arr = [...list];
  arr.sort((a, b) => {
    const av = opt.extract(a);
    const bv = opt.extract(b);
    let cmp;
    if (opt.numeric) cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    if (cmp === 0) {
      cmp = String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    }
    return sortDir === "desc" ? -cmp : cmp;
  });
  return arr;
}

function DraggableAchievement({ ach, from }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ach.id,
    data: { ach, from },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ touchAction: "none", opacity: isDragging ? 0.35 : 1 }}
      className="flex items-center gap-2 p-2 bg-black/40 hover:bg-black/60 border border-white/10 rounded cursor-grab active:cursor-grabbing"
      data-testid={`sorter-ach-${ach.id}`}
    >
      {ach.imageUrl && (
        <img
          src={ach.imageUrl}
          alt=""
          className="w-8 h-8 rounded object-cover shrink-0"
          onError={(e) => (e.target.style.display = "none")}
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-white truncate">{ach.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          {ach.score > 0 && (
            <span className="text-[10px] text-yellow-400/70">{ach.score}G</span>
          )}
          {ach.dlcName && (
            <span
              className="text-[10px] px-1 py-[1px] rounded bg-purple-500/20 text-purple-300 truncate max-w-[110px]"
              title={ach.dlcName}
            >
              {ach.dlcName}
            </span>
          )}
          {ach.isUnlocked && (
            <span className="text-[10px] text-emerald-400/70">✓</span>
          )}
        </div>
      </div>
    </div>
  );
}

function SubgroupDropTarget({ sub, count, selected, onSelect, isOver }) {
  const { setNodeRef } = useDroppable({ id: `sub:${sub.id}`, data: { subId: sub.id } });
  const Icon = getSubgroupIconComp(sub.icon);
  const color = sub.color || "#06B6D4";
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded border transition-colors text-left ${
        selected
          ? "border-cyan-400/60 bg-cyan-500/10"
          : isOver
          ? "border-cyan-400 bg-cyan-500/20"
          : "border-white/10 bg-black/40 hover:bg-black/60"
      }`}
      data-testid={`sorter-subgroup-${sub.id}`}
    >
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <span className="flex-1 text-sm text-white truncate">{sub.name}</span>
      <span className="text-[11px] text-white/50 shrink-0">{count}</span>
    </button>
  );
}

function UngroupedDropTarget({ children, isOver }) {
  const { setNodeRef } = useDroppable({ id: `sub:${UNGROUPED_ID}`, data: { subId: UNGROUPED_ID } });
  return (
    <div
      ref={setNodeRef}
      className={`h-full rounded border transition-colors ${
        isOver ? "border-cyan-400 bg-cyan-500/10" : "border-dashed border-white/10 bg-black/20"
      }`}
    >
      {children}
    </div>
  );
}

export function OrganizerSubgroupSorterDialog({
  open,
  onOpenChange,
  gameId,
  section,
  api,
  onChanged,
}) {
  // Local mirror of the section (subsections + achievements) so we can update
  // optimistically without waiting for a full fetchData round-trip on every drop.
  const [subsections, setSubsections] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [newSubName, setNewSubName] = useState("");
  const [dragging, setDragging] = useState(null);
  const [overId, setOverId] = useState(null);
  // Sort choice applies to BOTH the LEFT (in-subgroup) and RIGHT (ungrouped)
  // panels so users don't have to configure two dropdowns.
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Load current state when opened
  useEffect(() => {
    if (!open || !section) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${api}/games/${gameId}/achievements-grouped`);
        if (cancel) return;
        const fullSection = (res.data?.baseGame?.sections || []).find((s) => s.id === section.id);
        const subs = fullSection?.subsections || [];
        // Achievements assigned to this organizer (base game side + DLC side)
        const inSection = [];
        (fullSection?.achievements || []).forEach((a) => inSection.push(a));
        (res.data?.dlcs || []).forEach((dlc) => {
          (dlc.achievements || []).forEach((a) => {
            if (a.sectionId === section.id) inSection.push(a);
          });
        });
        setSubsections(subs);
        setAchievements(inSection);
        // Default-select first subgroup if any
        setSelectedSubId((prev) => prev || subs[0]?.id || null);
      } catch (e) {
        toast.error("Failed to load organizer contents");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, section, gameId, api]);

  const buckets = useMemo(() => {
    const map = new Map();
    subsections.forEach((s) => map.set(s.id, []));
    const ungrouped = [];
    for (const a of achievements) {
      const sid = a.subSectionId;
      if (sid && map.has(sid)) map.get(sid).push(a);
      else ungrouped.push(a);
    }
    // Apply the shared sort order to every bucket so panels stay consistent.
    map.forEach((arr, key) => map.set(key, sortAchievements(arr, sortBy, sortDir)));
    return { map, ungrouped: sortAchievements(ungrouped, sortBy, sortDir) };
  }, [achievements, subsections, sortBy, sortDir]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleAddSubgroup = async () => {
    const name = newSubName.trim();
    if (!name) return;
    try {
      setSaving(true);
      const res = await axios.post(
        `${api}/games/${gameId}/sections/${section.id}/subsections`,
        { name }
      );
      setSubsections((prev) => [...prev, res.data]);
      setSelectedSubId(res.data.id);
      setNewSubName("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add subgroup");
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (event) => {
    setOverId(null);
    setDragging(null);
    const { active, over } = event;
    if (!over || !active) return;
    const overSubId = over.data?.current?.subId;
    if (!overSubId) return;
    const ach = active.data?.current?.ach;
    if (!ach) return;

    // No-op if dropped in the same bucket the ach is already in
    const currentSub = ach.subSectionId || UNGROUPED_ID;
    if (currentSub === overSubId) return;

    // Optimistic move
    const nextSub = overSubId === UNGROUPED_ID ? null : overSubId;
    setAchievements((prev) =>
      prev.map((a) => (a.id === ach.id ? { ...a, subSectionId: nextSub } : a))
    );

    try {
      if (nextSub === null) {
        await axios.post(`${api}/games/${gameId}/bulk-move`, {
          achievementIds: [ach.id],
          targetType: "section",
          targetId: section.id,
        });
      } else {
        await axios.post(`${api}/games/${gameId}/bulk-move`, {
          achievementIds: [ach.id],
          targetType: "subsection",
          targetId: nextSub,
          parentSectionId: section.id,
        });
      }
      onChanged && onChanged();
    } catch (e) {
      // Roll back on failure
      setAchievements((prev) =>
        prev.map((a) => (a.id === ach.id ? { ...a, subSectionId: ach.subSectionId } : a))
      );
      toast.error(e.response?.data?.detail || "Move failed");
    }
  };

  const selectedList =
    selectedSubId && buckets.map.has(selectedSubId) ? buckets.map.get(selectedSubId) : [];
  const selectedSub = subsections.find((s) => s.id === selectedSubId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-5xl w-[95vw] h-[85vh] flex flex-col"
        data-testid="subgroup-sorter-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-cyan-400" />
            Sort into Subgroups · {section?.name || "Organizer"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Drag achievements between the panels. Click a subgroup to focus it on the left.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-white/10">
          <Input
            value={newSubName}
            onChange={(e) => setNewSubName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddSubgroup()}
            placeholder="New subgroup name…"
            className="flex-1 min-w-[180px] h-9 bg-black/50 border-white/10 text-sm"
            data-testid="sorter-new-subgroup-input"
          />
          <Button
            onClick={handleAddSubgroup}
            disabled={saving || !newSubName.trim()}
            className="h-9 bg-cyan-600 hover:bg-cyan-700"
            data-testid="sorter-add-subgroup-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add subgroup
          </Button>

          {/* Shared sort — applies to both the in-subgroup and ungrouped
              panels so the eye can always follow the same rule. */}
          <div className="flex items-center gap-1 ml-auto">
            <ArrowUpDown className="w-3.5 h-3.5 text-white/40" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger
                className="h-9 w-[140px] bg-black/50 border-white/10 text-xs"
                data-testid="sorter-sort-by"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="h-9 w-9 border-white/10 bg-black/50 hover:bg-black/70"
              title={sortDir === "asc" ? "Ascending — click for descending" : "Descending — click for ascending"}
              data-testid="sorter-sort-dir"
            >
              {sortDir === "asc" ? (
                <ArrowUp className="w-3.5 h-3.5" />
              ) : (
                <ArrowDown className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => setDragging(e.active?.data?.current?.ach || null)}
            onDragOver={(e) => setOverId(e.over?.id || null)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => {
              setDragging(null);
              setOverId(null);
            }}
          >
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 min-h-0 pt-3">
              {/* LEFT: achievements inside the selected subgroup */}
              <div className="flex flex-col min-h-0 rounded border border-white/10 bg-black/20 overflow-hidden">
                <div className="px-3 py-2 border-b border-white/10 bg-white/5 flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-white/50">In subgroup</span>
                  <span className="ml-auto text-[11px] text-cyan-300 truncate">
                    {selectedSub ? selectedSub.name : "Select a subgroup →"}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                  {!selectedSub && (
                    <p className="text-[11px] text-white/40 text-center py-6">
                      Pick a subgroup from the middle panel to see its achievements here.
                    </p>
                  )}
                  {selectedSub && selectedList.length === 0 && (
                    <p className="text-[11px] text-white/40 text-center py-6">
                      No achievements yet — drag some over from the right panel.
                    </p>
                  )}
                  {selectedList.map((a) => (
                    <DraggableAchievement key={a.id} ach={a} from={selectedSubId} />
                  ))}
                </div>
              </div>

              {/* MIDDLE: subgroup list */}
              <div className="flex flex-col min-h-0 rounded border border-white/10 bg-black/20 overflow-hidden">
                <div className="px-3 py-2 border-b border-white/10 bg-white/5">
                  <span className="text-xs uppercase tracking-wider text-white/50">Subgroups</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                  {subsections.length === 0 && (
                    <p className="text-[11px] text-white/40 text-center py-6">
                      No subgroups yet. Add one above to start bucketing achievements.
                    </p>
                  )}
                  {subsections.map((sub) => (
                    <SubgroupDropTarget
                      key={sub.id}
                      sub={sub}
                      count={buckets.map.get(sub.id)?.length || 0}
                      selected={selectedSubId === sub.id}
                      onSelect={() => setSelectedSubId(sub.id)}
                      isOver={overId === `sub:${sub.id}`}
                    />
                  ))}
                </div>
              </div>

              {/* RIGHT: ungrouped achievements */}
              <div className="flex flex-col min-h-0 rounded border border-white/10 bg-black/20 overflow-hidden">
                <div className="px-3 py-2 border-b border-white/10 bg-white/5 flex items-center gap-2">
                  <Inbox className="w-3.5 h-3.5 text-white/50" />
                  <span className="text-xs uppercase tracking-wider text-white/50">Ungrouped</span>
                  <span className="ml-auto text-[11px] text-white/50">
                    {buckets.ungrouped.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  <UngroupedDropTarget isOver={overId === `sub:${UNGROUPED_ID}`}>
                    <div className="p-1 space-y-1.5 min-h-full">
                      {buckets.ungrouped.length === 0 ? (
                        <p className="text-[11px] text-white/40 text-center py-6">
                          Everything is bucketed! Drop items here to move them back to Ungrouped.
                        </p>
                      ) : (
                        buckets.ungrouped.map((a) => (
                          <DraggableAchievement key={a.id} ach={a} from={UNGROUPED_ID} />
                        ))
                      )}
                    </div>
                  </UngroupedDropTarget>
                </div>
              </div>
            </div>

            {/* Portal the DragOverlay out of the Radix Dialog's transformed
                container, otherwise `position: fixed` on the overlay becomes
                relative to the dialog (not the viewport) and the overlay
                drifts away from the cursor. */}
            {createPortal(
              <DragOverlay
                dropAnimation={null}
                modifiers={[snapCenterToCursor]}
              >
                {dragging && (
                  <div className="p-2 bg-cyan-900/85 border border-cyan-400/60 rounded shadow-lg max-w-[300px] pointer-events-none">
                    <p className="text-xs text-white truncate">{dragging.name}</p>
                  </div>
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        )}

        <div className="flex items-center justify-end pt-3 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/70 hover:text-white"
          >
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OrganizerSubgroupSorterDialog;
